import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaSun, 
  FaMoon, 
  FaSun as FaUV, 
  FaWind, 
  FaLeaf,
  FaEllipsisV,
  FaTree,
  FaSeedling
} from 'react-icons/fa';

const TodaysHighlights = ({ weather, airQuality, pollen }) => {
  if (!weather) return null;

  const highlights = [
    {
      icon: <FaSun className="text-lg text-yellow-500" />,
      label: 'Sunrise',
      value: weather.sunrise
    },
    {
      icon: <FaMoon className="text-lg text-blue-500" />,
      label: 'Sunset',
      value: weather.sunset
    },
    {
      icon: <FaUV className="text-lg text-orange-500" />,
      label: 'UV Index',
      value: weather.uvIndex,
      level: weather.uvLevel,
      colorClass: weather.uvLevel?.color || 'uv-low'
    }
  ];

  const airQualityHighlight = airQuality ? {
    icon: <FaWind className="text-lg text-green-500" />,
    label: 'Air Quality',
    value: airQuality.level?.level || 'Good',
    level: airQuality.level,
    colorClass: airQuality.level?.color || 'air-good'
  } : null;

  const pollenHighlights = pollen ? [
    {
      icon: <FaLeaf className="text-lg text-green-600" />,
      label: 'Grass Pollen',
      value: pollen.grass.value,
      level: pollen.grass.level,
      colorClass: pollen.grass.level?.color || 'pollen-low'
    },
    {
      icon: <FaTree className="text-lg text-green-700" />,
      label: 'Tree Pollen',
      value: pollen.tree.value,
      level: pollen.tree.level,
      colorClass: pollen.tree.level?.color || 'pollen-low'
    },
    {
      icon: <FaSeedling className="text-lg text-green-800" />,
      label: 'Weed Pollen',
      value: pollen.weed.value,
      level: pollen.weed.level,
      colorClass: pollen.weed.level?.color || 'pollen-low'
    }
  ] : [];

  const allHighlights = [...highlights, airQualityHighlight, ...pollenHighlights].filter(Boolean);

  return (
    <motion.div 
      className="glass-card p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Today's Highlights</h3>
        <button className="text-gray-400 hover:text-gray-600">
          <FaEllipsisV />
        </button>
      </div>
      
      <div className="space-y-4">
        {allHighlights.map((highlight, index) => (
          <motion.div
            key={highlight.label}
            className="flex items-center justify-between p-4 glass-card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
          >
            <div className="flex items-center space-x-3">
              {highlight.icon}
              <span className="text-gray-800 font-medium">{highlight.label}</span>
            </div>
            
            <div className="text-right">
              {highlight.level ? (
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-semibold text-gray-800">
                    {highlight.value}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    highlight.colorClass === 'uv-low' || highlight.colorClass === 'air-good' || highlight.colorClass === 'pollen-low'
                      ? 'bg-green-100 text-green-800'
                      : highlight.colorClass === 'uv-moderate' || highlight.colorClass === 'air-moderate' || highlight.colorClass === 'pollen-moderate'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {highlight.level.level}
                  </span>
                </div>
              ) : (
                <span className="text-lg font-semibold text-gray-800">
                  {highlight.value}
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default TodaysHighlights;
